import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { Row } from '@tanstack/react-table';
import { Plus } from 'lucide-react';

import { LoadingErrorContentWrapper } from '@/components/blocks/loading-error-content-wrapper';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import ScenariosTable from '@/routes/(app)/(scenarios)/components/scenarios-table';
import { useScenariosList } from '@/routes/(app)/(scenarios)/hooks/use-scenarios-list';
import { type Scenario } from '@/routes/(app)/(scenarios)/types';

const ScenariosPage: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    scenarios,
    total,
    pagination,
    onPaginationChange,
    sorting,
    onSortingChange,
    nameSearch,
    onNameSearchChange,
    isNameSearchActive,
    isLoading,
    hasError,
    refetch,
  } = useScenariosList();

  const handleClickRow = useCallback(
    async (row: Row<Scenario>) => {
      await navigate({
        to: '/app/scenarios/$scenarioId',
        params: {
          scenarioId: row.original.id,
        },
      });
    },
    [navigate]
  );

  const handleNavigateToCreateScenario = useCallback(async () => {
    await navigate({ to: '/app/scenarios/create' });
  }, [navigate]);

  return (
    <main className='flex flex-col gap-4'>
      <div className='flex items-center justify-between gap-4'>
        <Input
          type='search'
          className='max-w-sm'
          value={nameSearch}
          placeholder={t('scenarios.search.placeholder')}
          aria-label={t('scenarios.search.placeholder')}
          onChange={(event) => {
            onNameSearchChange(event.target.value);
          }}
        />
        <Button
          className='w-fit'
          onClick={handleNavigateToCreateScenario}>
          <Plus />
          {t('navigation.scenarios.createButton')}
        </Button>
      </div>
      <LoadingErrorContentWrapper
        hasError={hasError}
        isLoading={isLoading}
        errorMessage={t('general.toast.fetchFailed')}
        refetch={refetch}>
        <ScenariosTable
          scenarios={scenarios}
          total={total}
          pagination={pagination}
          onPaginationChange={onPaginationChange}
          sorting={sorting}
          onSortingChange={onSortingChange}
          onClickRow={handleClickRow}
          empty={
            isNameSearchActive ? t('scenarios.search.noResults') : undefined
          }
        />
      </LoadingErrorContentWrapper>
    </main>
  );
};
export default ScenariosPage;
